import { DateTime } from "luxon";
import { useMemo } from "react";
import useSWR from "swr";

import { getProductStock, listProductStocks } from "@/features/product-stocks/api/productStocksApi";
import { useProductStocks } from "@/features/product-stocks/hooks/useProductStocks";
import { getProvisionStock, listProvisionStocks } from "@/features/provision-stocks/api/provisionStocksApi";
import { useProvisionStocks } from "@/features/provision-stocks/hooks/useProvisionStocks";
import type { Vending } from "@/features/vending/api/vendingApi";
import { useVendings } from "@/features/vending/hooks/useVending";
import { stockStatus, type StockStatus } from "@/lib/expiry";
import { toErrorMessage } from "@/lib/swr";
import type { ProductStockStateDto, ProvisionStockStateDto } from "@/types/stock.types";

/** Days of history the revenue bars cover, today included. */
export const REVENUE_DAYS = 7;

export type RevenuePoint = {
  /** ISO day, used as the stable key. */
  day: string;
  /** Short French weekday, for the axis. */
  label: string;
  /** Spelled-out day, for the tooltip where there is room. */
  fullLabel: string;
  revenue: number;
  isToday: boolean;
};

export type StockAlert = {
  id: string;
  name: string;
  quantity: number;
  measurementUnit: string;
  expiryDate?: string | null;
  status: StockStatus;
  origin: "provision" | "product";
  stockId: string;
};

export type RecentSale = {
  key: string;
  label: string;
  at: string;
  total: number;
  lineCount: number;
};

/**
 * The list endpoints only carry stock identities; the totals and the batches
 * live on the per-stock state, so the ids are resolved in one batch here.
 */
function useStockStates() {
  const { stocks: provisionStocks, isLoading: loadingProvisionList } = useProvisionStocks();
  const { stocks: productStocks, isLoading: loadingProductList } = useProductStocks();

  const provisionIds = provisionStocks.map((stock) => stock.id);
  const productIds = productStocks.map((stock) => stock.id);

  const {
    data: provisionStates,
    error: provisionError,
    isLoading: loadingProvisionStates,
  } = useSWR(provisionIds.length ? ["dashboard/provision-stock-states", provisionIds.join(",")] : null, () =>
    Promise.all(provisionIds.map(getProvisionStock)),
  );

  const {
    data: productStates,
    error: productError,
    isLoading: loadingProductStates,
  } = useSWR(productIds.length ? ["dashboard/product-stock-states", productIds.join(",")] : null, () =>
    Promise.all(productIds.map(getProductStock)),
  );

  return {
    provisionStates: provisionStates ?? [],
    productStates: productStates ?? [],
    /** Surfaced rather than swallowed: a failed load would read as an empty stock. */
    error: toErrorMessage(provisionError ?? productError),
    isLoading: loadingProvisionList || loadingProductList || loadingProvisionStates || loadingProductStates,
  };
}

function toAlerts(provisionStates: ProvisionStockStateDto[], productStates: ProductStockStateDto[]): StockAlert[] {
  const alerts: StockAlert[] = [];

  for (const stock of provisionStates) {
    for (const item of stock.items) {
      const status = stockStatus(item.quantity, item.expiryDate);
      if (status === "ok") continue;
      alerts.push({
        id: `${stock.id}:${item.provision.id}`,
        name: item.provision.name,
        quantity: item.quantity,
        measurementUnit: item.provision.measurementUnit,
        expiryDate: item.expiryDate,
        status,
        origin: "provision",
        stockId: stock.id,
      });
    }
  }

  for (const stock of productStates) {
    for (const item of stock.items) {
      const status = stockStatus(item.quantity, item.expiryDate);
      if (status === "ok") continue;
      alerts.push({
        id: `${stock.id}:${item.product.id}`,
        name: item.product.name,
        quantity: item.quantity,
        measurementUnit: item.product.measurementUnit,
        expiryDate: item.expiryDate,
        status,
        origin: "product",
        stockId: stock.id,
      });
    }
  }

  /** Expired first, then what runs out, then what is about to expire. */
  const severity: Record<StockStatus, number> = { expired: 0, out: 1, expiring: 2, ok: 3 };
  return alerts.sort((a, b) => severity[a.status] - severity[b.status]);
}

function saleDate(vending: Vending): string {
  return vending.vendedAt || vending.createdAt;
}

function toRevenueSeries(vendings: Vending[]): RevenuePoint[] {
  const totals = new Map<string, number>();

  for (const vending of vendings) {
    const day = DateTime.fromISO(saleDate(vending)).toISODate();
    if (!day) continue;
    totals.set(day, (totals.get(day) ?? 0) + vending.price * vending.quantity);
  }

  const today = DateTime.now().startOf("day");

  return Array.from({ length: REVENUE_DAYS }, (_, index) => {
    const date = today.minus({ days: REVENUE_DAYS - 1 - index });
    const day = date.toISODate()!;
    return {
      day,
      label: date.setLocale("fr").toFormat("ccc"),
      fullLabel: date.setLocale("fr").toFormat("cccc d LLLL"),
      revenue: totals.get(day) ?? 0,
      isToday: index === REVENUE_DAYS - 1,
    };
  });
}

/**
 * The API stores one row per product sold with no basket aggregate, so rows
 * sharing a label and a minute are folded back into the sale they came from.
 */
function toRecentSales(vendings: Vending[], limit: number): RecentSale[] {
  const groups = new Map<string, Vending[]>();

  for (const vending of vendings) {
    const minute = saleDate(vending).slice(0, 16);
    const key = `${vending.label ?? ""}@${minute}`;
    groups.set(key, [...(groups.get(key) ?? []), vending]);
  }

  return Array.from(groups.entries())
    .map(([key, lines]) => ({
      key,
      label: lines[0]?.label || "Vente",
      at: lines[0] ? saleDate(lines[0]) : "",
      total: lines.reduce((sum, line) => sum + line.price * line.quantity, 0),
      lineCount: lines.length,
    }))
    .sort((a, b) => b.at.localeCompare(a.at))
    .slice(0, limit);
}

export function useDashboard(recentSalesLimit = 5) {
  const { vendings, isLoading: loadingVendings, error: vendingError, refresh } = useVendings();
  const { provisionStates, productStates, error: stockError, isLoading: loadingStocks } = useStockStates();

  const revenueSeries = useMemo(() => toRevenueSeries(vendings), [vendings]);
  const alerts = useMemo(() => toAlerts(provisionStates, productStates), [provisionStates, productStates]);
  const recentSales = useMemo(() => toRecentSales(vendings, recentSalesLimit), [vendings, recentSalesLimit]);

  const today = useMemo(() => {
    const start = DateTime.now().startOf("day");
    const lines = vendings.filter((vending) => DateTime.fromISO(saleDate(vending)) >= start);

    return {
      revenue: lines.reduce((sum, line) => sum + line.price * line.quantity, 0),
      saleCount: new Set(lines.map((line) => `${line.label ?? ""}@${saleDate(line).slice(0, 16)}`)).size,
    };
  }, [vendings]);

  const stockValue = useMemo(
    () => ({
      provision: provisionStates.reduce((sum, stock) => sum + stock.monetaryValue, 0),
      product: productStates.reduce((sum, stock) => sum + stock.monetaryValue, 0),
    }),
    [provisionStates, productStates],
  );

  return {
    today,
    stockValue,
    revenueSeries,
    alerts,
    recentSales,
    isLoading: loadingVendings || loadingStocks,
    error: vendingError ?? stockError,
    refresh,
  };
}
